import React, { memo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';

import moment from 'moment';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import Button from 'modules/views/shared/Button';
import Tabs from 'modules/views/shared/Tabs';
import TextField from 'modules/views/shared/TextField';
import MobileInput from 'modules/views/shared/MobileInput';
import WithBranding from 'modules/views/shared/WithBranding';
import Link from 'modules/views/shared/Link';
import CompanySelectionModal from './CompanySelectionModal';
import Warning from 'modules/views/shared/Warning';
import FullscreenLoader from 'modules/views/shared/FullscreenLoader';

import loginSchema from './schema';
import useForm from 'hooks/useForm';
import useSubmitOnEnter from 'hooks/useSubmitOnEnter';
import { useLogin } from './hooks';
import { useStyles } from './styles';
import { ROUTES } from 'constants/routes';
import { useAppSelector } from 'modules/state-mgmt/store';

const packageJson = require('../../../../../package.json');

const Login = () => {
  const classes = useStyles();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAppSelector(state => ({
    user: state.user,
  }));
  const isAdmin = user.isAdmin;

  const { isLoading, isResuming, login, companies, showCompanyModal, handleCompanySelection, closeCompanyModal } =
    useLogin({
      onFailure: backendErrors => {
        setErrors(backendErrors);
      },
    });

  const onSubmit = (values: any) => {
    login(values.loginWith.value === 'email' ? values.email : values.mobilePhoneNumber, values.password);
  };

  const { values, errors, handleSubmit, setValue, setErrors } = useForm({
    initialValues: {
      loginWith: location.state?.mobilePhoneNumber
        ? { label: t('login.loginWithPhone'), value: 'mobilePhoneNumber' }
        : { label: t('login.loginWithEmail'), value: 'email' },
      email: location.state?.email || '',
      mobilePhoneNumber: location.state?.mobilePhoneNumber || '',
      password: '',
    },
    validationSchema: loginSchema(t),
    onSubmit: onSubmit,
  });

  const submitOnEnter = useSubmitOnEnter(handleSubmit, isLoading);

  const onTabChange = (selectedTab: any) => {
    setValue('loginWith', selectedTab);
    setErrors({});
  };

  const TabContent = (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Warning
          text={
            <p>
              {t('login.companyAdminsWarning')}{' '}
              <a
                className={classes.companyAdminLink}
                href={process.env.REACT_APP_FREEDOM_WEB_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('common.here')}
              </a>
            </p>
          }
          styleClass={classes.companyAdminsWarning}
        />
      </Grid>
      {values.loginWith.value === 'email' ? (
        <Grid item xs={12}>
          <TextField
            label={t('login.email')}
            name="email"
            required={true}
            value={values.email}
            error={errors.email?.message}
            testId="email-input"
            onChange={e => setValue('email', e.target.value?.trim())}
            autoComplete={isAdmin ? 'one-time-code' : 'username'}
          />
        </Grid>
      ) : (
        <Grid item xs={12}>
          <MobileInput
            label={t('login.phoneNumber')}
            required={true}
            placeholder={t('login.phoneNumber')}
            value={values.mobilePhoneNumber}
            error={errors.mobilePhoneNumber?.message}
            testId="mobilePhoneNumber-input"
            onChange={({ phone }) => setValue('mobilePhoneNumber', phone)}
            autoComplete={isAdmin ? 'one-time-code' : 'tel'}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <TextField
          label={t('login.password')}
          name={isAdmin ? 'credential' : 'password'}
          required={true}
          value={values.password}
          error={errors.password?.message}
          type={isAdmin ? 'text' : 'password'}
          maskText={isAdmin}
          testId={isAdmin ? 'credential-input' : 'password-input'}
          onChange={e => setValue('password', e.target.value)}
          onKeyDown={submitOnEnter}
          inputProps={{ maxLength: 256 }}
          autoComplete={isAdmin ? 'off' : 'current-password'}
        />
      </Grid>
      <Grid item xs={12}>
        <Link
          disabled={isLoading}
          to={ROUTES.FORGOT_PASSWORD.path}
          label={t('login.forgotPassword')}
          data-testid="forgot-password-link"
        />
      </Grid>
    </Grid>
  );

  const Content = (
    <>
      <ContentHeader title={t('login.title')} subtitle={t('login.subtitle')} />
      <Tabs
        tabs={[
          {
            value: 'email',
            label: t('login.loginWithEmail'),
            content: TabContent,
            tabProps: { 'data-testid': 'login-tab-email' },
          },
          {
            value: 'mobilePhoneNumber',
            label: t('login.loginWithPhone'),
            content: TabContent,
            tabProps: { 'data-testid': 'login-tab-phone' },
          },
        ]}
        selectedTab={values.loginWith}
        tabsContainerStyleClass={classes.tabsParent}
        onChange={onTabChange}
      />

      <div className={classes.buttonParent}>
        <Button
          label={t('login.buttons.login')}
          hasEndArrow={true}
          isLoading={isLoading}
          styleClass={classes.button}
          testId="login-submit-button"
          onClick={handleSubmit}
        />
      </div>
      <div className={classes.buildInfo}>
        Build Version {packageJson.buildVersion} | Published on{' '}
        {packageJson.buildDate && moment(packageJson.buildDate).format('MMM DD, YYYY')}
      </div>
    </>
  );

  return (
    <>
      <WithBranding Content={Content} />
      <CompanySelectionModal
        companies={companies}
        isVisible={showCompanyModal}
        isLoading={isLoading}
        onSelectCompany={handleCompanySelection}
        onClose={closeCompanyModal}
      />
      {/* Only the CH-4652 resume earns an overlay — an ordinary login is fast enough that the
          button spinner alone is the right amount of feedback. */}
      {isResuming && <FullscreenLoader transparent={true} />}
    </>
  );
};

export default memo(Login);
